import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import type { ChecklistItem } from '../types';

type Props = {
  item: ChecklistItem;
  onEdit: (item: ChecklistItem) => void;
  onDelete: (id: string) => Promise<void>;
  disabled?: boolean;
};

export const ItemActionsMenu = ({
  item,
  onEdit,
  onDelete,
  disabled
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(item);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(item.id);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        disabled={disabled}
        sx={{
          width: { xs: 24, sm: 28 },
          height: { xs: 24, sm: 28 },
          color: 'text.secondary',
          opacity: isMobile ? 0.7 : 0,
          transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          flexShrink: 0,

          '.card-container:hover &': {
            opacity: 1
          },

          '@media (hover: none)': {
            opacity: 0.7
          },

          '&:hover': {
            backgroundColor: 'action.hover',
            color: 'text.primary'
          }
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            boxShadow: 4,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            minWidth: 160,
            mt: 0.5
          }
        }}
        MenuListProps={{
          sx: {
            py: 1
          }
        }}
      >
        <MenuItem
          onClick={handleEdit}
          disabled={disabled}
          sx={{
            fontSize: '14px',
            py: 1.5,
            px: 2,
            minHeight: 'auto',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <EditIcon
              fontSize="small"
              sx={{ opacity: 0.6, color: 'inherit' }}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('checklist:form.edit')}
            primaryTypographyProps={{
              fontSize: 14,
              fontWeight: 400
            }}
          />
        </MenuItem>

        <MenuItem
          onClick={handleDeleteClick}
          disabled={disabled}
          sx={{
            fontSize: '14px',
            py: 1.5,
            px: 2,
            minHeight: 'auto',
            '&:hover': {
              backgroundColor: 'error.light',
              color: 'error.contrastText'
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DeleteIcon
              fontSize="small"
              sx={{
                opacity: 0.6,
                color: theme.palette.error.main
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('checklist:form.delete')}
            primaryTypographyProps={{
              fontSize: 14,
              fontWeight: 400,
              color: theme.palette.error.main
            }}
          />
        </MenuItem>
      </Menu>

      <DeleteConfirmationModal
        open={showDeleteModal}
        itemText={item.text}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};
